import { v4 as uuid } from 'uuid'
import type { EndpointsRegistry, FetchClient } from '~/api/types'
import { MapboxEndpoints } from '~/api/endpoints/mapbox-endpoints'
import type {
  SearchBoxRetrieveResponse,
  SearchBoxSuggestion,
  SearchBoxSuggestionResponse
} from '@mapbox/search-js-core'
import type { AddressSuggestion } from '~/composables/map/types/search'
import { toLngLat } from '~/utils/map/map'
import { useSentry } from '~/composables/monitoring/useSentry'

export interface MapboxService {
  getAddressSuggestions(query: string): Promise<AddressSuggestion[]>

  searchAddress(item: AddressSuggestion): Promise<[number, number] | null>

  reverseGeocodePlace(lng: number, lat: number, language?: string): Promise<string | null>
}

interface GeocodeV6Response {
  features?: Array<{
    properties?: {
      name?: string
      place_formatted?: string
      context?: { region?: { name?: string } }
    }
  }>
}

export const registerEndpoints = (client: FetchClient, registry: EndpointsRegistry): MapboxService => {
  const authStore = useAuthStore()
  const { captureException } = useSentry()

  let sessionToken: string | null = null
  let lastActivity = 0

  const config = useRuntimeConfig().public
  const token = config.map.apiKey

  const getSessionToken = (): string => {
    const now = Date.now()
    const oneHour = 60 * 60 * 1000

    if (!sessionToken || now - lastActivity > oneHour) {
      sessionToken = uuid()
    }

    lastActivity = now
    return sessionToken
  }

  return {
    async getAddressSuggestions(query: string): Promise<AddressSuggestion[]> {
      if (!query.trim()) return []

      const { lng, lat } = toLngLat(authStore.licenceOrigin)
      const request = registry.createRequest(MapboxEndpoints.FETCH_SUGGESTIONS, {
        queryParams: {
          q: query,
          types: 'address,poi,street',
          proximity: `${lng},${lat}`,
          session_token: getSessionToken(),
          access_token: token
        }
      })

      try {
        const { url, method, headers, body } = request
        const res = await client<SearchBoxSuggestionResponse>(url, { method, headers, body })
        return (res?.suggestions ?? []).map((s) => ({
          id: s.mapbox_id,
          name: s.name,
          placeName: s.place_formatted,
          raw: s
        }))
      } catch (err) {
        captureException(err as Error, {
          service: 'mapbox',
          operation: 'suggest',
          query
        })
        return []
      }
    },

    async reverseGeocodePlace(lng: number, lat: number, language?: string): Promise<string | null> {
      const request = registry.createRequest(MapboxEndpoints.REVERSE_GEOCODE, {
        queryParams: {
          longitude: lng,
          latitude: lat,
          types: 'place',
          limit: 1,
          ...(language ? { language } : {}),
          access_token: token
        }
      })

      try {
        const { url, method, headers, body } = request
        const res = await client<GeocodeV6Response>(url, { method, headers, body })
        const props = res?.features?.[0]?.properties
        if (!props?.name) return props?.place_formatted ?? null
        const region = props.context?.region?.name
        return region && region !== props.name ? `${props.name}, ${region}` : props.name
      } catch (err) {
        captureException(err as Error, {
          service: 'mapbox',
          operation: 'reverseGeocode',
          lng,
          lat
        })
        return null
      }
    },

    async searchAddress(item: AddressSuggestion): Promise<[number, number] | null> {
      const raw = item.raw as SearchBoxSuggestion
      if (!raw?.mapbox_id) return null

      const request = registry.createRequest(MapboxEndpoints.RETRIEVE_SUGGESTION, {
        params: { mapbox_id: raw.mapbox_id },
        queryParams: {
          session_token: getSessionToken(),
          access_token: token
        }
      })

      try {
        const { url, method, headers, body } = request
        const res = await client<SearchBoxRetrieveResponse>(url, { method, headers, body })
        const [lng, lat] = res?.features?.[0]?.geometry?.coordinates ?? []
        return lng !== undefined && lat !== undefined ? [lng, lat] : null
      } catch (err) {
        captureException(err as Error, {
          service: 'mapbox',
          operation: 'retrieve',
          mapbox_id: raw.mapbox_id
        })
        return null
      }
    }
  }
}
